import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API, eur } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Car, FileText, FileSpreadsheet, ChevronLeft, Wrench, Landmark } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };
const VEH_INITIALS = { "GLE personenwagen": "GLE", "Vito lichte vracht": "Vito" };
const VEH_COLOR = { "GLE personenwagen": "bg-orange-500", "Vito lichte vracht": "bg-emerald-600" };

export default function FactuurboekAutokosten() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith("/rapporten") ? "/rapporten" : "/factuurboek";
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [vehicle, setVehicle] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/autokosten/summary", { params: { from_date: from, to_date: to } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    return vehicle ? data.vehicles.filter((v) => v.vehicle === vehicle) : data.vehicles;
  }, [data, vehicle]);
  const tabs = useMemo(() => ["", ...(data?.available_vehicles || [])], [data]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ from_date: from, to_date: to, format: fmt });
      if (vehicle) params.set("vehicle", vehicle);
      const res = await fetch(`${API}/factuurboek/autokosten/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Autokosten_${from}_${to}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Autokosten geëxporteerd (${fmt.toUpperCase()})`);
    } catch { toast.error("Kon rapport niet exporteren"); }
    finally { setBusy(""); }
  };

  const typeIcon = (t) => t === "leasing" ? <Landmark className="h-4 w-4" /> : t === "onderhoud" ? <Wrench className="h-4 w-4" /> : <FileText className="h-4 w-4" />;

  return (
    <div className="space-y-6" data-testid="autokosten-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate(`${base}/overzicht`)} data-testid="auto-back" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1">
            <ChevronLeft className="h-4 w-4" /> Terug naar overzicht
          </button>
          <p className="text-xs font-semibold tracking-widest text-amber-700 uppercase">Autokosten per auto</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Car className="h-7 w-7 text-amber-600" /> GLE en Vito uitgesplitst
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy === "pdf"} data-testid="auto-export-pdf" onClick={() => download("pdf")}>
            <FileText className="h-4 w-4 mr-1.5 text-red-600" /> {busy === "pdf" ? "Bezig…" : "Export PDF"}
          </Button>
          <Button variant="outline" disabled={busy === "xlsx"} data-testid="auto-export-xlsx" onClick={() => download("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {busy === "xlsx" ? "Bezig…" : "Excel"}
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="auto-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="auto-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <Button className="bg-amber-600 hover:bg-amber-700" data-testid="auto-refresh" onClick={load}>Tonen</Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap border-b pb-3">
        {tabs.map((t) => (
          <Button key={t || "all"} size="sm" variant={vehicle === t ? "default" : "outline"}
            className={vehicle === t ? "bg-amber-600 hover:bg-amber-700" : ""}
            data-testid={`auto-tab-${t ? t.split(" ")[0] : "alle"}`} onClick={() => setVehicle(t)}>
            {t ? t.split(" ")[0] : "Alle"}
          </Button>
        ))}
      </div>

      {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> :
        visible.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="auto-empty">
            Nog geen autokosten voor deze selectie. Zet bij het verwerken de kostencategorie op "Autokosten" en kies het voertuig.
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {visible.map((v) => (
              <Card key={v.vehicle} className="shadow-sm overflow-hidden" data-testid={`auto-card-${v.vehicle.split(" ")[0]}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-11 w-11 rounded-md ${VEH_COLOR[v.vehicle] || "bg-slate-500"} flex items-center justify-center text-white font-semibold text-xs shrink-0`}>
                      {VEH_INITIALS[v.vehicle] || "?"}
                    </div>
                    <div>
                      <CardTitle className="font-heading text-lg">{v.vehicle}{v.plate ? <span className="text-muted-foreground font-normal text-sm ml-2">{v.plate}</span> : null}</CardTitle>
                      <div className="text-2xl font-heading font-semibold tabular-nums" data-testid={`auto-total-${v.vehicle.split(" ")[0]}`}>{eur(v.base)}</div>
                      <div className="text-xs text-muted-foreground">{v.count} facturen · {eur(v.vat)} btw</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {v.by_type.map((bt) => (
                      <div key={bt.type} className="rounded-lg border p-3" data-testid={`auto-type-${v.vehicle.split(" ")[0]}-${bt.type}`}>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">{typeIcon(bt.type)} {bt.label}</div>
                        <div className="text-lg font-semibold tabular-nums">{eur(bt.base)}</div>
                        <div className="text-xs text-muted-foreground">{bt.count} lijnen</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-24">Datum</TableHead><TableHead>Soort · leverancier</TableHead><TableHead className="text-right">Bedrag</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {v.lines.map((ln, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm text-muted-foreground">{nlDate(ln.date)}</TableCell>
                            <TableCell className="text-sm"><span className="font-medium">{ln.type_label}</span> · {ln.supplier}</TableCell>
                            <TableCell className="text-right tabular-nums">{eur(ln.base)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
